import * as Sentry from '@sentry/react'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  Separator,
  SidebarTrigger
} from '@repo/ui'
import { Link } from '@tanstack/react-router'
import { MessageSquare } from 'lucide-react'
import { Fragment, useEffect, useRef } from 'react'
import type { ReactNode } from 'react'

export interface BreadcrumbEntry {
  label: string
  to?: string
}

const MAX_BREADCRUMB_CHARS = 40

function truncateLabel(label: string): string {
  return label.length > MAX_BREADCRUMB_CHARS ? label.slice(0, MAX_BREADCRUMB_CHARS) + '...' : label
}

function FeedbackButton() {
  const ref = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const feedback = Sentry.getFeedback()
    if (!feedback || !ref.current) return
    return feedback.attachTo(ref.current)
  }, [])

  return (
    <Button ref={ref} variant="ghost" size="sm" className="gap-1.5 text-muted-foreground">
      <MessageSquare className="size-4" />
      Feedback
    </Button>
  )
}

export function PageHeader({ breadcrumbs, actions }: { breadcrumbs: Array<BreadcrumbEntry>; actions?: ReactNode }) {
  return (
    <header className="flex h-14 shrink-0 items-center gap-2 border-b px-4 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12 md:pl-14">
      <SidebarTrigger className="-ml-1 md:hidden" />
      <Separator orientation="vertical" className="mr-2 h-4 md:hidden" />
      <Breadcrumb>
        <BreadcrumbList>
          {breadcrumbs.map((crumb, index) => (
            <Fragment key={index}>
              {index > 0 && <BreadcrumbSeparator />}
              <BreadcrumbItem title={crumb.label}>
                {crumb.to ? (
                  <BreadcrumbLink asChild>
                    <Link to={crumb.to}>{truncateLabel(crumb.label)}</Link>
                  </BreadcrumbLink>
                ) : (
                  <BreadcrumbPage>{truncateLabel(crumb.label)}</BreadcrumbPage>
                )}
              </BreadcrumbItem>
            </Fragment>
          ))}
        </BreadcrumbList>
      </Breadcrumb>
      {/* Breadcrumbs don't provide heading semantics — screen readers need an h1 for page identification */}
      <h1 className="sr-only">{breadcrumbs[breadcrumbs.length - 1]?.label}</h1>
      <div className="ml-auto flex items-center gap-2">
        {import.meta.env.PROD && <FeedbackButton />}
        {actions && actions}
      </div>
    </header>
  )
}
